import React, { useState, useEffect, useRef } from "react";
import { Result } from "../../types";
import PDFExport from "./AutomationPDFExport";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";
import { Bar, Doughnut, Radar } from "react-chartjs-2";
import {
  DataObject,
  Speed,
  Analytics,
  LocalActivity,
  AutoAwesome,
  Settings,
  Chat,
  Timeline,
  Campaign,
  Build,
  Celebration,
  Assignment,
  AttachMoney,
  Business,
  GpsFixed,
  Assessment,
  Code,
  Dashboard,
  Email,
} from "@mui/icons-material";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler
);

interface ResultsPageProps {
  results: Result[];
  onRestart: () => void;
}

const ResultsPage: React.FC<ResultsPageProps> = ({ results, onRestart }) => {
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedTab, setSelectedTab] = useState("recommendations");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setAnimationStep((prev) => (prev < 4 ? prev + 1 : prev));
    }, 500);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Update recommendations generation function to better match questions
  const generateDetailedRecommendations = () => {
    const recommendations = [];
    const answers = results.map((r) => r.answer.toLowerCase());

    // Descriptive Analytics (Historical data)
    if (
      answers.some(
        (a) =>
          a.includes("past") ||
          a.includes("happened") ||
          a.includes("performance")
      )
    ) {
      recommendations.push({
        category: "Descriptive Analytics",
        title: "Historical Performance Reports",
        description:
          "Automated reports showing what happened in your business over time with clear visualizations and trends.",
        priority: "High",
        icon: <Assessment />,
        benefits: [
          "Understand past performance patterns",
          "Track KPIs and metrics over time",
          "Identify seasonal trends and patterns",
          "Create executive-ready reports automatically",
        ],
        implementation: "4-6 weeks",
        roi: "180% within 6 months",
        score: 85,
      });
    }

    // Diagnostic Analytics (Why things happened)
    if (
      answers.some(
        (a) =>
          a.includes("why") || a.includes("patterns") || a.includes("trends")
      )
    ) {
      recommendations.push({
        category: "Diagnostic Analytics",
        title: "Root Cause Analysis Tools",
        description:
          "Advanced analytics that help you understand why things happened and identify the factors driving your business outcomes.",
        priority: "High",
        icon: <Analytics />,
        benefits: [
          "Discover why performance changed",
          "Identify key drivers of success/failure",
          "Understand customer behavior patterns",
          "Make data-driven decisions with confidence",
        ],
        implementation: "6-8 weeks",
        roi: "220% within 8 months",
        score: 92,
      });
    }

    // Predictive Analytics
    if (
      answers.some(
        (a) =>
          a.includes("predict") || a.includes("future") || a.includes("next")
      )
    ) {
      recommendations.push({
        category: "Predictive Analytics",
        title: "Future Trend Forecasting",
        description:
          "Machine learning models that predict future outcomes based on historical data and current trends.",
        priority: "Medium",
        icon: <Timeline />,
        benefits: [
          "Predict future sales and revenue",
          "Forecast customer demand",
          "Anticipate market trends",
          "Plan resources more effectively",
        ],
        implementation: "10-14 weeks",
        roi: "250% within 12 months",
        score: 88,
      });
    }

    // Prescriptive Analytics
    if (
      answers.some(
        (a) =>
          a.includes("should do") ||
          a.includes("recommendations") ||
          a.includes("actions")
      )
    ) {
      recommendations.push({
        category: "Prescriptive Analytics",
        title: "Automated Recommendations Engine",
        description:
          "AI-powered system that not only predicts what will happen but recommends specific actions to take.",
        priority: "Medium",
        icon: <AutoAwesome />,
        benefits: [
          "Get specific action recommendations",
          "Optimize business processes automatically",
          "Personalize customer experiences",
          "Maximize ROI through data-driven actions",
        ],
        implementation: "12-16 weeks",
        roi: "300% within 15 months",
        score: 94,
      });
    }

    // Dashboard/Visualization
    if (
      answers.some(
        (a) =>
          a.includes("dashboard") ||
          a.includes("interactive") ||
          a.includes("visual")
      )
    ) {
      recommendations.push({
        category: "Data Visualization",
        title: "Interactive Dashboard Platform",
        description:
          "Self-service analytics platform with drag-and-drop dashboard creation and real-time data visualization.",
        priority: "High",
        icon: <Dashboard />,
        benefits: [
          "Create custom dashboards without IT",
          "Real-time data visualization",
          "Interactive charts and filters",
          "Mobile-responsive design",
        ],
        implementation: "6-8 weeks",
        roi: "190% within 7 months",
        score: 87,
      });
    }

    // Real-time Analytics
    if (
      answers.some(
        (a) =>
          a.includes("real-time") ||
          a.includes("alerts") ||
          a.includes("unusual")
      )
    ) {
      recommendations.push({
        category: "Real-time Analytics",
        title: "Live Data Streaming & Alerts",
        description:
          "Real-time data processing with instant alerts and notifications when key metrics exceed thresholds.",
        priority: "Medium",
        icon: <Speed />,
        benefits: [
          "Monitor business metrics in real-time",
          "Instant alerts for critical events",
          "React quickly to market changes",
          "Prevent issues before they escalate",
        ],
        implementation: "8-10 weeks",
        roi: "200% within 9 months",
        score: 83,
      });
    }

    // Text Analytics for unstructured data
    if (
      answers.some(
        (a) =>
          a.includes("text") ||
          a.includes("feedback") ||
          a.includes("reviews") ||
          a.includes("emails")
      )
    ) {
      recommendations.push({
        category: "Text Analytics",
        title: "Unstructured Data Processing",
        description:
          "Natural language processing tools to analyze customer feedback, reviews, emails, and social media content.",
        priority: "Medium",
        icon: <Email />,
        benefits: [
          "Analyze customer sentiment automatically",
          "Extract insights from text data",
          "Monitor brand reputation",
          "Understand customer feedback at scale",
        ],
        implementation: "8-12 weeks",
        roi: "170% within 10 months",
        score: 81,
      });
    }

    // Data Preparation
    if (
      answers.some(
        (a) =>
          a.includes("cleaning") ||
          a.includes("organizing") ||
          a.includes("both types")
      )
    ) {
      recommendations.push({
        category: "Data Preparation",
        title: "Automated Data Pipeline",
        description:
          "Automated data cleaning, transformation, and integration tools that prepare your data for analysis.",
        priority: "High",
        icon: <Settings />,
        benefits: [
          "Clean and prepare data automatically",
          "Integrate data from multiple sources",
          "Ensure data quality and consistency",
          "Reduce manual data preparation time by 80%",
        ],
        implementation: "6-8 weeks",
        roi: "185% within 7 months",
        score: 86,
      });
    }

    return recommendations;
  };

  const recommendations = generateDetailedRecommendations();
  const categories = [...new Set(recommendations.map((r) => r.category))];

  // Remove this unused priorityData variable (around line 237):
  // const priorityData = {
  //   labels: [
  //     "High Priority (Immediate Implementation)",
  //     "Medium Priority (Phase 2)",
  //     "Low Priority (Future Consideration)",
  //   ],
  //   datasets: [
  //     {
  //       data: [
  //         recommendations.filter((r) => r.priority === "High").length,
  //         recommendations.filter((r) => r.priority === "Medium").length,
  //         recommendations.filter((r) => r.priority === "Low").length,
  //       ],
  //       backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
  //       borderWidth: 0,
  //       hoverOffset: 10,
  //     },
  //   ],
  // };

  // Keep the other chart data objects that are actually being used:
  const categoryData = {
    labels: categories,
    datasets: [
      {
        label: "Number of Recommended Features",
        data: categories.map(
          (cat) => recommendations.filter((r) => r.category === cat).length
        ),
        backgroundColor: "rgba(99, 102, 241, 0.8)",
        borderColor: "#6366f1",
        borderWidth: 2,
        borderRadius: 8,
      },
    ],
  };

  const businessImpactData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Business Impact Score (0-100)",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.3)",
        borderColor: "#10b981",
        pointBackgroundColor: "#10b981",
        pointBorderColor: "#fff",
        pointHoverBackgroundColor: "#fff",
        pointHoverBorderColor: "#10b981",
      },
    ],
  };

  const roiTimelineData = {
    labels: recommendations.map((r) =>
      r.title.split(" ").slice(0, 2).join(" ")
    ),
    datasets: [
      {
        label: "Expected ROI (%)",
        data: recommendations.map((r) => {
          // Extract ROI percentage from strings like "150% within 6 months"
          const roiMatch = r.roi.match(/(\d+)%/);
          return roiMatch ? parseInt(roiMatch[1]) : 100;
        }),
        backgroundColor: "rgba(245, 158, 11, 0.6)",
        borderColor: "#f59e0b",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
      {
        label: "Implementation Score",
        data: recommendations.map((r) => r.score),
        backgroundColor: "rgba(16, 185, 129, 0.6)",
        borderColor: "#10b981",
        borderWidth: 2,
        tension: 0.4,
        fill: true,
      },
    ],
  };

  // Priority distribution based on actual user responses
  const responseBasedPriority = {
    labels: [
      "Immediate Impact (High ROI)",
      "Strategic Growth (Medium ROI)",
      "Future Enhancement (Lower ROI)",
    ],
    datasets: [
      {
        data: [
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue >= 120 && roiValue < 150;
          }).length,
          recommendations.filter((r) => {
            const roiMatch = r.roi.match(/(\d+)%/);
            const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;
            return roiValue < 120;
          }).length,
        ],
        backgroundColor: ["#ef4444", "#f59e0b", "#10b981"],
        borderWidth: 0,
        hoverOffset: 10,
      },
    ],
  };

  return (
    <div
      ref={resultsRef}
      style={{
        background: "linear-gradient(135deg, #667eea 0%, #152A53 100%)",
        minHeight: "100vh",
        padding: isMobile ? "1rem" : "2rem",
      }}
    >
      <div style={{ maxWidth: "1400px", margin: "0 auto" }}>
        {/* Enhanced Header with responsive design */}
        <div
          className={animationStep >= 0 ? "fade-in" : ""}
          style={{
            textAlign: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            color: "white",
          }}
        >
          <div
            style={{
              fontSize: isMobile ? "2.5rem" : "4rem",
              marginBottom: "1rem",
            }}
          >
            <Celebration
              sx={{ fontSize: isMobile ? "2.5rem" : "4rem", color: "white" }}
            />
          </div>
          <h1
            style={{
              fontFamily: "var(--font-headline)",
              fontSize: isMobile
                ? "var(--font-size-headline-mobile)"
                : "var(--font-size-headline)",
              fontWeight: "800",
              marginBottom: "1rem",
              background:
                "linear-gradient(35deg,rgb(215, 227, 255) 0%,rgb(155, 188, 255) 30%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              lineHeight: "1.2",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Your Personalized Analytics Roadmap
          </h1>
          <p
            style={{
              fontFamily: "var(--font-body)",
              fontSize: isMobile
                ? "var(--font-size-body-mobile)"
                : "var(--font-size-body)",
              opacity: 0.9,
              maxWidth: "700px",
              margin: "0 auto 2rem",
              lineHeight: "1.6",
              padding: isMobile ? "0 1rem" : "0",
            }}
          >
            Based on your responses, we've created a comprehensive analytics
            strategy tailored to your data needs with detailed implementation
            guidance and ROI projections.
          </p>

          {/* Key Statistics - Responsive Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile
                ? "repeat(2, 1fr)"
                : "repeat(auto-fit, minmax(200px, 1fr))",
              gap: isMobile ? "0.75rem" : "1rem",
              maxWidth: "800px",
              margin: "0 auto",
              padding: isMobile ? "0 0.5rem" : "0",
            }}
          >
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Assignment
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                {recommendations.length}
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Automation Features
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
              }}
            >
              <Speed
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                4-16 weeks
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Implementation
              </div>
            </div>
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.15)",
                padding: isMobile ? "1rem" : "1.5rem",
                borderRadius: "16px",
                backdropFilter: "blur(10px)",
                gridColumn: isMobile ? "1 / -1" : "auto",
              }}
            >
              <AttachMoney
                sx={{
                  fontSize: isMobile ? "1.5rem" : "2rem",
                  marginBottom: "0.5rem",
                  color: "white",
                }}
              />
              <div
                style={{
                  fontSize: isMobile ? "1.2rem" : "1.5rem",
                  fontWeight: "bold",
                }}
              >
                150%+
              </div>
              <div
                style={{
                  fontSize: isMobile ? "0.8rem" : "0.9rem",
                  opacity: 0.8,
                }}
              >
                Expected ROI within 6-12 months
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Tabs - Responsive */}
        <div
          className={animationStep >= 1 ? "fade-in" : ""}
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: isMobile ? "2rem" : "3rem",
            opacity: animationStep >= 1 ? 1 : 0,
            transform:
              animationStep >= 1 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            flexWrap: "wrap",
            gap: isMobile ? "0.5rem" : "0",
          }}
        >
          {["recommendations", "analytics", "your-responses"].map((tab) => (
            <button
              key={tab}
              onClick={() => setSelectedTab(tab)}
              style={{
                padding: isMobile ? "0.75rem 1rem" : "1rem 2rem",
                margin: isMobile ? "0" : "0 0.5rem",
                backgroundColor:
                  selectedTab === tab
                    ? "rgba(255, 255, 255, 0.9)"
                    : "rgba(255, 255, 255, 0.1)",
                color: selectedTab === tab ? "#1f2937" : "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: isMobile ? "0.9rem" : "1.1rem",
                fontWeight: "600",
                transition: "all 0.3s ease",
                backdropFilter: "blur(10px)",
                textTransform: "capitalize",
                minWidth: isMobile ? "auto" : "120px",
              }}
            >
              {tab === "your-responses" ? "Your Responses" : tab}
            </button>
          ))}
        </div>

        {/* Content based on selected tab */}
        {selectedTab === "recommendations" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Detailed Recommendations - Responsive Grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(400px, 1fr))",
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
            >
              {recommendations.map((recommendation, index) => (
                <div
                  key={index}
                  style={{
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    borderRadius: "20px",
                    padding: isMobile ? "1.5rem" : "2.5rem",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    transition: "transform 0.3s ease",
                    transitionDelay: `${index * 0.1}s`,
                    position: "relative",
                    overflow: "hidden",
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(-8px)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isMobile) {
                      e.currentTarget.style.transform = "translateY(0)";
                    }
                  }}
                >
                  {/* Priority Badge - Responsive */}
                  <div
                    style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      padding: isMobile ? "0.3rem 0.75rem" : "0.5rem 1rem",
                      borderRadius: "20px",
                      fontSize: isMobile ? "0.7rem" : "0.8rem",
                      fontWeight: "600",
                      backgroundColor:
                        recommendation.priority === "High"
                          ? "#fee2e2"
                          : recommendation.priority === "Medium"
                          ? "#fef3c7"
                          : "#d1fae5",
                      color:
                        recommendation.priority === "High"
                          ? "#dc2626"
                          : recommendation.priority === "Medium"
                          ? "#d97706"
                          : "#059669",
                    }}
                  >
                    {recommendation.priority} Priority
                  </div>

                  {/* Header - Responsive */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: isMobile ? "flex-start" : "center",
                      marginBottom: "1.5rem",
                      flexDirection: isMobile ? "column" : "row",
                      textAlign: isMobile ? "center" : "left",
                    }}
                  >
                    <div
                      style={{
                        fontSize: isMobile ? "2rem" : "3rem",
                        marginRight: isMobile ? "0" : "1rem",
                        marginBottom: isMobile ? "1rem" : "0",
                        color: "#c9a750ff",
                      }}
                    >
                      {React.cloneElement(recommendation.icon, {
                        sx: { fontSize: isMobile ? "2rem" : "3rem" },
                      })}
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: "0.9rem",
                          color: "#c9a750ff",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        {recommendation.category}
                      </div>
                      <h3
                        style={{
                          color: "#1f2937",
                          margin: 0,
                          fontSize: isMobile ? "1.2rem" : "1.4rem",
                          lineHeight: "1.3",
                        }}
                      >
                        {recommendation.title}
                      </h3>
                    </div>
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      color: "#6b7280",
                      lineHeight: "1.6",
                      marginBottom: "1.5rem",
                      fontSize: isMobile ? "0.9rem" : "1rem",
                    }}
                  >
                    {recommendation.description}
                  </p>

                  {/* Benefits - Responsive Grid */}
                  <div style={{ marginBottom: "1.5rem" }}>
                    <h4
                      style={{
                        color: "#1f2937",
                        marginBottom: "0.75rem",
                        fontSize: "1rem",
                      }}
                    >
                      Key Benefits:
                    </h4>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: isMobile
                          ? "1fr"
                          : "repeat(2, 1fr)",
                        gap: "0.5rem",
                      }}
                    >
                      {recommendation.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            fontSize: isMobile ? "0.8rem" : "0.9rem",
                            color: "#4b5563",
                          }}
                        >
                          <span
                            style={{ color: "#10b981", marginRight: "0.5rem" }}
                          >
                            ✓
                          </span>
                          {benefit}
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Details - Responsive */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
                      gap: "1rem",
                      padding: "1rem",
                      backgroundColor: "rgba(201, 167, 80, 0.1)",
                      borderRadius: "12px",
                    }}
                  >
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Timeline
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#1f2937",
                        }}
                      >
                        {recommendation.implementation}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Expected ROI
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#059669",
                        }}
                      >
                        {recommendation.roi}
                      </div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div
                        style={{
                          fontSize: "0.8rem",
                          color: "#6b7280",
                          marginBottom: "0.25rem",
                        }}
                      >
                        Impact Score
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "0.9rem" : "1rem",
                          fontWeight: "600",
                          color: "#c9a750ff",
                        }}
                      >
                        {recommendation.score}/100
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {selectedTab === "analytics" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Charts Grid - Responsive */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", // Keep 2-column layout
                gap: isMobile ? "1.5rem" : "2rem",
                marginBottom: isMobile ? "2rem" : "3rem",
              }}
              className="analytics-container"
            >
              {/* Chart containers with responsive padding */}

              {/* Implementation Timeline Chart - KEEP THIS */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="implementation-timeline"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <GpsFixed
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Strategic Implementation Timeline
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Breakdown of automation recommendations by implementation
                  timeframe
                </p>
                {/* Create implementation timeline data */}
                <Bar
                  data={{
                    labels: ["Implementation Timeline"],
                    datasets: [
                      {
                        label: "Quick Wins (Days to 2 weeks)",
                        data: [
                          recommendations.filter(
                            (r) =>
                              r.implementation.includes("day") ||
                              (r.implementation.includes("week") &&
                                parseInt(r.implementation) <= 2)
                          ).length,
                        ],
                        backgroundColor: "#10b981", // Green
                        stack: "Stack 0",
                        barThickness: 60,
                      },
                      {
                        label: "Short-term (2-4 weeks)",
                        data: [
                          recommendations.filter(
                            (r) =>
                              r.implementation.includes("2-4") ||
                              r.implementation.includes("3-5")
                          ).length,
                        ],
                        backgroundColor: "#3b82f6", // Blue
                        stack: "Stack 0",
                        barThickness: 60,
                      },
                      {
                        label: "Medium-term (1-2 months)",
                        data: [
                          recommendations.filter(
                            (r) =>
                              r.implementation.includes("4-8") ||
                              r.implementation.includes("3-6")
                          ).length,
                        ],
                        backgroundColor: "#f59e0b", // Orange
                        stack: "Stack 0",
                        barThickness: 60,
                      },
                      {
                        label: "Long-term (2+ months)",
                        data: [
                          recommendations.filter(
                            (r) =>
                              r.implementation.includes("6-10") ||
                              parseInt(r.implementation) > 6
                          ).length,
                        ],
                        backgroundColor: "#ef4444", // Red
                        stack: "Stack 0",
                        barThickness: 60,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y" as const,
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: {
                        position: "bottom" as const,
                        labels: {
                          usePointStyle: true,
                          padding: isMobile ? 10 : 20,
                          font: {
                            size: isMobile ? 10 : 12,
                          },
                        },
                      },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `${context.dataset.label}: ${context.parsed.x} features`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        stacked: true,
                        ticks: {
                          stepSize: 1,
                        },
                      },
                      y: {
                        stacked: true,
                      },
                    },
                  }}
                />
              </div>

              {/* Business Impact Score Chart - KEEP THIS */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="business-impact"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Business
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Strategic Impact Score
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Ranked business impact of recommended automation features
                </p>
                <Bar
                  data={{
                    // Sort recommendations by score
                    labels: recommendations
                      .sort((a, b) => b.score - a.score)
                      .map((r) =>
                        r.title.length > 25
                          ? r.title.substring(0, 25) + "..."
                          : r.title
                      ),
                    datasets: [
                      {
                        label: "Impact Score",
                        // Sort data to match labels
                        data: recommendations
                          .sort((a, b) => b.score - a.score)
                          .map((r) => r.score),
                        backgroundColor: recommendations
                          .sort((a, b) => b.score - a.score)
                          .map((r) => {
                            // Color based on score
                            if (r.score >= 90) return "#10b981"; // Green
                            if (r.score >= 85) return "#3b82f6"; // Blue
                            if (r.score >= 80) return "#f59e0b"; // Orange
                            return "#ef4444"; // Red
                          }),
                        borderWidth: 0,
                        borderRadius: 6,
                      },
                    ],
                  }}
                  options={{
                    indexAxis: "y" as const,
                    responsive: true,
                    maintainAspectRatio: true,
                    plugins: {
                      legend: { display: false },
                      tooltip: {
                        callbacks: {
                          label: function (context: any) {
                            return `Impact Score: ${context.parsed.x}/100`;
                          },
                        },
                      },
                    },
                    scales: {
                      x: {
                        beginAtZero: true,
                        max: 100,
                        grid: {
                          display: false,
                        },
                      },
                      y: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>

              {/* ROI vs Budget Analysis Chart - KEEP THIS */}
              <div
                style={{
                  backgroundColor: "rgba(255, 255, 255, 0.95)",
                  borderRadius: "20px",
                  padding: isMobile ? "1.5rem" : "2rem",
                  backdropFilter: "blur(10px)",
                }}
                data-chart="roi-budget"
              >
                <h3
                  style={{
                    marginBottom: "0.5rem",
                    color: "#1f2937",
                    display: "flex",
                    alignItems: "center",
                    fontFamily: "var(--font-headline)",
                    fontSize: isMobile
                      ? "calc(var(--font-size-headline-mobile) * 0.7)"
                      : "calc(var(--font-size-headline) * 0.7)",
                    fontWeight: "600",
                  }}
                >
                  <Timeline
                    sx={{
                      marginRight: "0.5rem",
                      color: "#c9a750ff",
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                    }}
                  />
                  Strategic Investment Planning
                </h3>
                <p
                  style={{
                    fontFamily: "var(--font-body)",
                    fontSize: isMobile
                      ? "var(--font-size-body-mobile)"
                      : "var(--font-size-body)",
                    color: "#6b7280",
                    marginBottom: "1.5rem",
                    lineHeight: "1.6",
                  }}
                >
                  Budget requirements mapped against expected ROI percentages
                </p>
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: "8px",
                    padding: "20px",
                    height: "250px",
                    position: "relative",
                    marginBottom: "10px",
                    backgroundColor: "#f9fafb",
                  }}
                >
                  {/* X and Y axis labels */}
                  <div
                    style={{
                      position: "absolute",
                      left: 0,
                      right: 0,
                      bottom: 0,
                      textAlign: "center",
                      fontWeight: "bold",
                      color: "#6b7280",
                      fontSize: "0.8rem",
                      padding: "5px",
                    }}
                  >
                    Implementation Complexity
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: 0,
                      display: "flex",
                      alignItems: "center",
                      transform: "rotate(-90deg) translateX(-50%)",
                      transformOrigin: "left center",
                      fontWeight: "bold",
                      color: "#6b7280",
                      fontSize: "0.8rem",
                    }}
                  >
                    ROI Percentage
                  </div>

                  {/* Quadrant labels */}
                  <div
                    style={{
                      position: "absolute",
                      top: "25%",
                      left: "25%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "0.7rem",
                      color: "#6b7280",
                    }}
                  >
                    Strategic Projects
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "25%",
                      left: "75%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "0.7rem",
                      color: "#6b7280",
                    }}
                  >
                    Quick Wins
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "75%",
                      left: "25%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "0.7rem",
                      color: "#6b7280",
                    }}
                  >
                    Reconsider
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: "75%",
                      left: "75%",
                      transform: "translate(-50%, -50%)",
                      fontSize: "0.7rem",
                      color: "#6b7280",
                    }}
                  >
                    Fill-ins
                  </div>

                  {/* Quadrant dividers */}
                  <div
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      right: 0,
                      borderTop: "1px dashed #d1d5db",
                    }}
                  ></div>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      bottom: 0,
                      left: "50%",
                      borderLeft: "1px dashed #d1d5db",
                    }}
                  ></div>

                  {/* Plot the recommendations as bubbles */}
                  {recommendations.map((rec, index) => {
                    // Extract ROI percentage
                    const roiMatch = rec.roi.match(/(\d+)%/);
                    const roiValue = roiMatch ? parseInt(roiMatch[1]) : 100;

                    // Calculate implementation complexity (100 - score)
                    const complexity = 100 - rec.score;

                    // Position based on ROI and complexity
                    const top = 100 - ((roiValue - 100) / 120) * 100; // Map ROI 100-220% to 100%-0%
                    const left = (complexity / 100) * 100; // Map complexity 0-100 to 0%-100%

                    // Size based on priority
                    const size =
                      rec.priority === "High"
                        ? 60
                        : rec.priority === "Medium"
                        ? 45
                        : 30;

                    // Color based on category
                    const categoryIndex = categories.indexOf(rec.category);
                    const colors = [
                      "#3b82f6",
                      "#10b981",
                      "#f59e0b",
                      "#ef4444",
                      "#8b5cf6",
                      "#ec4899",
                    ];
                    const color = colors[categoryIndex % colors.length];

                    return (
                      <div
                        key={index}
                        style={{
                          position: "absolute",
                          top: `${Math.min(Math.max(top, 10), 90)}%`,
                          left: `${Math.min(Math.max(left, 10), 90)}%`,
                          width: `${size}px`,
                          height: `${size}px`,
                          borderRadius: "50%",
                          backgroundColor: `${color}99`, // Add transparency
                          border: `2px solid ${color}`,
                          transform: "translate(-50%, -50%)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "bold",
                          zIndex: 2,
                          cursor: "pointer",
                          transition: "all 0.3s ease",
                        }}
                        title={`${rec.title}\nROI: ${roiValue}%\nImpact Score: ${rec.score}/100`}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform =
                            "translate(-50%, -50%) scale(1.2)";
                          e.currentTarget.style.zIndex = "10";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform =
                            "translate(-50%, -50%) scale(1)";
                          e.currentTarget.style.zIndex = "2";
                        }}
                      >
                        {index + 1}
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.8rem",
                    color: "#6b7280",
                  }}
                >
                  Bubble size indicates priority level • Numbers correspond to
                  recommendation order
                </div>
              </div>
            </div>

            {/* Chart Explanations - Responsive */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2rem",
                backdropFilter: "blur(10px)",
                marginBottom: "2rem",
              }}
            >
              <h3
                style={{
                  marginBottom: "1.5rem",
                  color: "#1f2937",
                  fontSize: isMobile ? "1.2rem" : "1.3rem",
                }}
              >
                📖 Understanding Your Automation Analytics
              </h3>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "1fr"
                    : "repeat(auto-fit, minmax(300px, 1fr))",
                  gap: "1.5rem",
                }}
              >
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    💰 ROI-Based Priority
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>Immediate Impact:</strong> High ROI features (150%+)
                    for quick wins
                    <br />
                    <strong>Strategic Growth:</strong> Medium ROI features
                    (120-149%) for sustained growth
                    <br />
                    <strong>Future Enhancement:</strong> Lower ROI features
                    (&lt;120%) for long-term value
                  </p>
                </div>
                <div>
                  <h4
                    style={{
                      color: "#c9a750ff",
                      marginBottom: "0.5rem",
                      fontSize: isMobile ? "1rem" : "1.1rem",
                    }}
                  >
                    📊 ROI vs Implementation
                  </h4>
                  <p
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                      lineHeight: "1.5",
                    }}
                  >
                    <strong>Orange bars:</strong> Expected ROI percentage within
                    timeframe
                    <br />
                    <strong>Green bars:</strong> Implementation difficulty score
                    (higher = easier)
                    <br />
                    This helps identify features with best ROI-to-effort ratio
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Your Responses section would follow similar responsive patterns */}
        {selectedTab === "your-responses" && (
          <div
            className={animationStep >= 2 ? "fade-in" : ""}
            style={{
              opacity: animationStep >= 2 ? 1 : 0,
              transform:
                animationStep >= 2 ? "translateY(0)" : "translateY(20px)",
              transition: "all 0.6s ease-in-out",
            }}
          >
            {/* Response content with mobile responsiveness */}
            <div
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                borderRadius: "20px",
                padding: isMobile ? "1.5rem" : "2.5rem",
                marginBottom: "2rem",
                backdropFilter: "blur(10px)",
              }}
            >
              {/* Response Summary Stats - Mobile responsive grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile
                    ? "repeat(2, 1fr)"
                    : "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: "1rem",
                  marginBottom: "2rem",
                  padding: isMobile ? "1rem" : "1.5rem",
                  backgroundColor: "rgba(201, 167, 80, 0.1)",
                  borderRadius: "12px",
                }}
              >
                {/* Stats cards with responsive text sizes */}
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#c9a750ff",
                    }}
                  >
                    📊
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {results.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Questions Answered
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#10b981",
                    }}
                  >
                    ⚡
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {
                      recommendations.filter((r) => r.priority === "High")
                        .length
                    }
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    High Priority Features
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#f59e0b",
                    }}
                  >
                    🎯
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {Math.round(
                      recommendations.reduce((acc, r) => acc + r.score, 0) /
                        recommendations.length
                    )}
                    %
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Avg. Match Score
                  </div>
                </div>
                <div style={{ textAlign: "center" }}>
                  <div
                    style={{
                      fontSize: isMobile ? "1.5rem" : "2rem",
                      color: "#8b5cf6",
                    }}
                  >
                    💼
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "1.2rem" : "1.5rem",
                      fontWeight: "bold",
                      color: "#1f2937",
                    }}
                  >
                    {categories.length}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "0.8rem" : "0.9rem",
                      color: "#6b7280",
                    }}
                  >
                    Business Areas Covered
                  </div>
                </div>
              </div>

              {/* Individual responses with mobile optimization */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr",
                  gap: isMobile ? "1rem" : "1.5rem",
                }}
              >
                {results.map((result, index) => (
                  <div
                    key={index}
                    style={{
                      padding: isMobile ? "1.5rem" : "2rem",
                      backgroundColor: "rgba(255, 255, 255, 0.8)",
                      borderRadius: "16px",
                      border: "1px solid rgba(201, 167, 80, 0.2)",
                      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.05)",
                    }}
                  >
                    {/* Question header with responsive layout */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        marginBottom: "1.5rem",
                        flexDirection: isMobile ? "column" : "row",
                      }}
                    >
                      <div
                        style={{
                          backgroundColor: "#c9a750ff",
                          color: "white",
                          borderRadius: "50%",
                          width: isMobile ? "28px" : "32px",
                          height: isMobile ? "28px" : "32px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          fontWeight: "600",
                          marginRight: isMobile ? "0" : "1rem",
                          marginBottom: isMobile ? "1rem" : "0",
                          flexShrink: 0,
                          alignSelf: isMobile ? "center" : "flex-start",
                        }}
                      >
                        Q{index + 1}
                      </div>
                      <div style={{ flex: 1 }}>
                        <h5
                          style={{
                            fontWeight: "700",
                            color: "#1f2937",
                            marginBottom: "0.5rem",
                            fontSize: isMobile ? "1rem" : "1.1rem",
                            lineHeight: "1.4",
                            textAlign: isMobile ? "center" : "left",
                          }}
                        >
                          {result.question}
                        </h5>
                      </div>
                    </div>

                    {/* Answer and impact sections with responsive styling */}
                    <div
                      style={{
                        backgroundColor: "rgba(201, 167, 80, 0.1)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #c9a750ff",
                        marginBottom: "1rem",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Your Response:
                      </div>
                      <div
                        style={{
                          color: "#c9a750ff",
                          fontWeight: "600",
                          fontSize: isMobile ? "0.9rem" : "1rem",
                        }}
                      >
                        {result.answer}
                      </div>
                    </div>

                    {/* Impact Analysis */}
                    <div
                      style={{
                        backgroundColor: "rgba(16, 185, 129, 0.05)",
                        padding: isMobile ? "1rem" : "1.25rem",
                        borderRadius: "12px",
                        borderLeft: "4px solid #10b981",
                      }}
                    >
                      <div
                        style={{
                          fontSize: isMobile ? "0.8rem" : "0.9rem",
                          color: "#374151",
                          fontWeight: "600",
                          marginBottom: "0.5rem",
                        }}
                      >
                        Impact on Recommendations:
                      </div>
                      <div
                        style={{
                          color: "#059669",
                          fontSize: isMobile ? "0.9rem" : "0.95rem",
                          lineHeight: "1.5",
                        }}
                      >
                        {/* Dynamic impact analysis based on the answer */}
                        {result.answer.toLowerCase().includes("data") ||
                        result.answer.toLowerCase().includes("entry")
                          ? "✓ Triggered data processing automation features for document handling and manual entry reduction"
                          : result.answer.toLowerCase().includes("workflow") ||
                            result.answer.toLowerCase().includes("process")
                          ? "✓ Recommended business process automation features for streamlined operations"
                          : result.answer.toLowerCase().includes("analytics") ||
                            result.answer.toLowerCase().includes("report")
                          ? "✓ Added advanced analytics and reporting automation capabilities to your recommendations"
                          : result.answer
                              .toLowerCase()
                              .includes("integration") ||
                            result.answer.toLowerCase().includes("connect")
                          ? "✓ Included system integration features for seamless data flow between applications"
                          : result.answer.toLowerCase().includes("no-code") ||
                            result.answer
                              .toLowerCase()
                              .includes("non-technical")
                          ? "✓ Focused on user-friendly no-code automation tools for your team"
                          : "✓ Contributed to your personalized automation feature selection and priority ranking"}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Action Buttons - Responsive */}
        <div
          style={{
            display: "flex",
            gap: isMobile ? "0.75rem" : "1rem",
            justifyContent: "center",
            flexWrap: "wrap",
            opacity: animationStep >= 3 ? 1 : 0,
            transform:
              animationStep >= 3 ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-in-out",
            padding: isMobile ? "0 1rem" : "0",
          }}
        >
          <PDFExport
            results={results}
            recommendations={recommendations}
            resultsRef={resultsRef}
          />
          <button
            onClick={onRestart}
            style={{
              padding: isMobile ? "0.75rem 1.5rem" : "1rem 2rem",
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
              border: "2px solid #c9a750ff",
              borderRadius: "12px",
              cursor: "pointer",
              fontSize: isMobile ? "1rem" : "1.1rem",
              fontWeight: "600",
              transition: "all 0.3s ease",
              backdropFilter: "blur(10px)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#c9a750ff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                "rgba(255, 255, 255, 0.2)";
            }}
          >
            Take Quiz Again
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResultsPage;
